/**
 * Organizations API Client
 * Handles all organization management API calls
 */

import apiClient from './client';

export const organizationsQueryKey = ['organizations'] as const;

export interface Organization {
  id: number;
  name: string;
  description: string | null;
  is_platform: boolean;
  max_users: number;
  max_devices: number;
  max_storage_gb: number;
  max_concurrent_streams: number;
  current_user_count: number;
  current_device_count: number;
  training_opt_in: boolean;
  created_at: string | null;
  updated_at: string | null;
  stats?: OrganizationStats;
}

export interface OrganizationStats {
  user_count: number;
  device_count: number;
  model_count: number;
  admin_count: number;
  active_user_count: number;
  unowned_device_count: number;
}

export interface CreateOrganizationRequest {
  name: string;
  description?: string;
  max_users?: number;
  max_devices?: number;
  max_storage_gb?: number;
  max_concurrent_streams?: number;
  training_opt_in?: boolean;
}

export interface UpdateOrganizationRequest {
  name?: string;
  description?: string | null;
  max_users?: number;
  max_devices?: number;
  max_storage_gb?: number;
  max_concurrent_streams?: number;
  training_opt_in?: boolean;
}

export interface OrganizationDeletionResource {
  key: string;
  label: string;
  count: number;
  consequence: string;
}

export interface OrganizationDeletionBlocker {
  code: string;
  message: string;
  resource_ids: string[];
}

export interface OrganizationDeletionImpact {
  organization_id: number;
  organization_name: string;
  irreversible: true;
  confirmation_name: string;
  resources: OrganizationDeletionResource[];
  blockers: OrganizationDeletionBlocker[];
  can_permanently_delete: boolean;
  scope_note: string;
}

export interface OrganizationDeleteResult {
  status: 'success';
  message: string;
  cleanup: Record<string, number>;
}

/**
 * Get all organizations
 * Admin sees all, users see only their own
 */
export const getOrganizations = async (): Promise<Organization[]> => {
  const response = await apiClient.get<Organization[]>('/api/v1/organizations/');
  return response.data;
};

/**
 * Get specific organization by ID
 */
export const getOrganization = async (id: number): Promise<Organization> => {
  const response = await apiClient.get<Organization>(`/api/v1/organizations/${id}`);
  return response.data;
};

/**
 * Create a new organization (platform scope only).
 */
export const createOrganization = async (data: CreateOrganizationRequest): Promise<Organization> => {
  const response = await apiClient.post<Organization>('/api/v1/organizations/', data);
  return response.data;
};

/**
 * Update an organization (platform scope only).
 */
export const updateOrganization = async (
  id: number,
  data: UpdateOrganizationRequest
): Promise<Organization> => {
  const response = await apiClient.patch<Organization>(`/api/v1/organizations/${id}`, data);
  return response.data;
};

/**
 * Read the current server-owned permanent-delete impact and blockers.
 */
export const getOrganizationDeletionImpact = async (
  id: number,
): Promise<OrganizationDeletionImpact> => {
  const response = await apiClient.get<OrganizationDeletionImpact>(
    `/api/v1/organizations/${id}/deletion-impact`,
  );
  return response.data;
};

/** Permanently delete after an exact name confirmation (platform scope only). */
export const deleteOrganization = async (
  id: number,
  confirmationName: string,
): Promise<OrganizationDeleteResult> => {
  const response = await apiClient.delete<OrganizationDeleteResult>(
    `/api/v1/organizations/${id}`,
    {
      data: { confirmation_name: confirmationName },
    },
  );
  return response.data;
};
